import { useState } from "react";
import { FieldValues } from "react-hook-form";
import { TypedFilter } from "odata-query";
import { taskService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import {
  CalendarEventType,
  ICalendarConfig,
  IDateRangeISO,
  IInspectionAttempt,
  IInspectionTemplate,
  IInspectionTrayConfig,
  IVerticalTabProps,
} from "@miplatform/types";
import {
  bugsnag,
  combineCalendarFilters,
  getDateDay,
  getDateISO,
  getDayTitle,
  getOrderTypeFilterTabs,
  groupBy,
  sortByAddress,
} from "@miplatform/utils";

export const useInspectionBoardViewModel = () => {
  const stateManager = useComponentState();
  const isLoading = stateManager.isBusy();

  const [inspectionDays, setInspectionDays] = useState<
    Record<string, Record<string, IInspectionAttempt[]>>
  >({});
  const [inspectionTemplates, setInspectionTemplates] = useState<
    Record<string, IInspectionTemplate>
  >({});
  const [inspectionBuckets, setInspectionBuckets] = useState<
    IInspectionTrayConfig[]
  >([]);
  const [templateFilterOptions, setTemplateFilterOptions] = useState<
    IVerticalTabProps<FieldValues>[]
  >([]);
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState<IDateRangeISO>({
    startDate: getDateISO(getDateDay()),
    endDate: null,
  });

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  const resetBoard = () => {
    setInspectionDays({});
    setInspectionBuckets([]);
    setSearch("");
  };

  const getInspectionDays = async (
    dateRange: IDateRangeISO,
    calendar?: ICalendarConfig
  ) => {
    const filters = combineCalendarFilters({
      dateRange,
      filtersArray: [calendar?.filter ?? {}],
      templateIdFilters: calendar?.orderTemplateIds,
    });

    const { data: inspections, status } = await getInspections({
      filters,
    });

    if (status === HTTP_STATUSES.OK) {
      const inspectionDays: Record<
        string,
        Record<string, IInspectionAttempt[]>
      > = {};

      getOrderTypeFilterOptions(inspections);

      inspections.forEach((inspection: IInspectionAttempt) => {
        const inspectionDay = getDayTitle(inspection.approvedStartDate);
        const { templateName } = inspection;
        if (inspectionDays[inspectionDay]) {
          if (inspectionDays[inspectionDay][templateName]) {
            inspectionDays[inspectionDay][templateName].push(inspection);
          } else {
            inspectionDays[inspectionDay][templateName] = [inspection];
          }
        } else {
          inspectionDays[inspectionDay] = {};
          inspectionDays[inspectionDay][templateName] = [inspection];
        }
      });

      const newInspectionDays: Record<
        string,
        Record<string, IInspectionAttempt[]>
      > = {};

      const processInspectionTemplate = (
        inspections: IInspectionAttempt[]
      ): IInspectionAttempt[] => {
        const groupedCommunities = groupBy(
          inspections,
          (insp: IInspectionAttempt) =>
            insp.home.communityName || "Unknown Community"
        );
        const sortedInspections = Object.values<IInspectionAttempt[]>(
          groupedCommunities
        ).flatMap((community: IInspectionAttempt[]) =>
          sortByAddress<IInspectionAttempt>(
            community,
            (insp) => insp.home.address?.addressLine1 || ""
          )
        );
        return sortedInspections;
      };

      const processInspectionDay = (
        day: string,
        templates: Record<string, IInspectionAttempt[]>
      ) => {
        newInspectionDays[day] = {};
        Object.entries(templates).forEach(([template, inspections]) => {
          newInspectionDays[day][template] =
            processInspectionTemplate(inspections);
        });
      };

      Object.entries(inspectionDays).forEach(([day, templates]) => {
        processInspectionDay(day, templates);
      });

      setInspectionDays(newInspectionDays);
    }
  };

  const getOrderTypeFilterOptions = (inspections: IInspectionAttempt[]) => {
    const filterOptions = getOrderTypeFilterTabs(inspections);
    setTemplateFilterOptions(filterOptions);
  };

  const getInspections = ({
    filters,
    sort,
  }: {
    filters: string | TypedFilter<IInspectionAttempt>;
    sort?: string;
  }) => {
    return stateManager.run(async () => {
      return taskService.inspection.getAllAttempts({
        sort,
        filters: filters as any, // cast to any to satisfy type
      });
    });
  };

  const getInspectionBuckets = async (
    eventType: CalendarEventType,
    calendar: ICalendarConfig
  ) => {
    if (!calendar.trays?.length) return [];

    const trayBuckets: IInspectionTrayConfig[] = [];

    const trayPromises = calendar.trays.map(
      async (tray: IInspectionTrayConfig) => {
        try {
          const filterStringParts = [];
          if (tray?.filter) {
            filterStringParts.push(`(${tray?.filter})`);
          }
          if (eventType && calendar.orderTemplateIds) {
            filterStringParts.push(
              `templateId in (${calendar.orderTemplateIds})`
            );
          }
          const filterString = filterStringParts.join(" and ");

          const response = await getInspections({
            filters: filterString,
            ...(tray.sort &&
              typeof tray.sort === "string" && { sort: tray.sort }),
          });

          if (response.status === HTTP_STATUSES.OK) {
            return {
              tray,
              data: response.data,
            };
          }
        } catch (trayError) {
          bugsnag.setContext("inspectionsProvider::getTrayBucketsData");
          bugsnag.notify(
            trayError instanceof Error
              ? trayError
              : new Error(String(trayError))
          );
        }

        return {
          tray,
          data: [],
        };
      }
    );

    try {
      const trayResults = await Promise.all(trayPromises);

      trayResults.forEach(({ tray, data }) => {
        trayBuckets.push({
          id: tray.id,
          isActive: tray.isActive,
          isDefault: tray.isDefault,
          name: tray.name,
          collapsible: tray.collapsible,
          displayOrder: tray.displayOrder,
          headerColor: tray.headerColor,
          filter: tray.filter,
          headerBackgroundColor: tray.headerBackgroundColor,
          contentBackgroundColor: tray.contentBackgroundColor,
          countBackgroundColor: tray.countBackgroundColor,
          countColor: tray.countColor,
          hideWhenEmpty: tray.hideWhenEmpty,
          data,
        });
      });

      trayBuckets.sort((a, b) => a.displayOrder - b.displayOrder);
    } catch (error) {
      bugsnag.setContext("useInspectionBoardViewModel::getTrayBucketsData");
      bugsnag.notify(error instanceof Error ? error : new Error(String(error)));
    }

    setInspectionBuckets(trayBuckets);
    return trayBuckets;
  };

  const getInspectionTemplates = () => {
    return stateManager.run(async () => {
      const response = await taskService.inspectionTemplates.getAll();

      if (response.status === HTTP_STATUSES.OK) {
        const templates: Record<string, IInspectionTemplate> = {};
        response.data.forEach((template: IInspectionTemplate) => {
          templates[template.name] = template;
        });
        setInspectionTemplates(templates);
      }
    });
  };

  const onDateRangeChange = (newDateRange: IDateRangeISO) => {
    setDateRange(newDateRange);
  };

  return {
    dateRange,
    getInspectionTemplates,
    getInspectionBuckets,
    getInspectionDays,
    inspectionDays,
    inspectionBuckets,
    inspectionTemplates,
    onDateRangeChange,
    handleSearch,
    search,
    isLoading,
    templateFilterOptions,
    resetBoard,
  };
};
